import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Chip, Alert, LinearProgress,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
  TablePagination, Collapse, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  PlayArrow as ResumeIcon,
  ArrowBack as BackIcon,
  CheckCircle as DoneIcon,
  ErrorOutlined as FailIcon,
  Sync as RunningIcon,
  HighlightOff as SkippedIcon,
  HourglassBottom as CancellingIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as InsightIcon,
} from '@mui/icons-material';
import {
  getAutoEnrichmentRun, listAutoEnrichmentItems, cancelAutoEnrichmentRun,
  resumeAutoEnrichmentRun, updateAutoEnrichmentRun, deleteAutoEnrichmentRun,
  getAiProviders, getLead,
} from '../services/api';
import { useDialog } from '../components/DialogProvider';

const STATUS_CHIP_COLOR = {
  Pending: 'default',
  Running: 'info',
  Cancelling: 'warning',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const STATUS_LABEL = {
  Cancelling: 'Cancel requested…',
};

const ITEM_STATUS_COLOR = {
  Pending: 'default',
  Processing: 'info',
  Succeeded: 'success',
  Failed: 'error',
  Skipped: 'warning',
};

// One-letter sigil for compact Status chip on small viewports.
const ITEM_STATUS_SIGIL = {
  Pending: 'P',
  Processing: '…',
  Succeeded: '✓',
  Failed: '✕',
  Skipped: '⊘',
};

const ICP_FIT_COLOR = {
  CurrentFit: 'success',
  FuturePotential: 'info',
  NotAFit: 'error',
  Unknown: 'default',
};

// Short token for compact ICP Fit chip.
const ICP_FIT_SHORT = {
  CurrentFit: 'CF',
  FuturePotential: 'FP',
  NotAFit: 'NF',
  Unknown: 'U',
};

function fmtSecs(s) {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ${s % 60}s`;
}

// Live elapsed seconds, monotonic across resumes (matches fmtDuration accounting).
function liveElapsedSecs(start, end, accumulatedSeconds) {
  const acc = accumulatedSeconds || 0;
  if (end) return acc > 0
    ? acc
    : (start ? Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000)) : 0);
  if (!start) return acc;
  return acc + Math.max(0, Math.round((Date.now() - new Date(start).getTime()) / 1000));
}

// Returns { remainingSecs, finishAt } or null when ETA is not meaningful
// (no progress yet, nothing left, paused/terminal, or interval would dominate).
function computeEta(job) {
  if (!job) return null;
  if (job.status !== 'Running') return null;
  const total = job.total || 0;
  const processed = job.processed || 0;
  const remaining = total - processed;
  if (processed <= 0 || remaining <= 0) return null;
  const elapsed = liveElapsedSecs(job.startedAt, job.finishedAt, job.accumulatedSeconds);
  if (elapsed <= 0) return null;
  const perLead = elapsed / processed; // already includes inter-lead interval
  const remainingSecs = Math.max(1, Math.round(perLead * remaining));
  return { remainingSecs, finishAt: new Date(Date.now() + remainingSecs * 1000) };
}

// Duration is monotonic across resume:
//  - terminal:     accumulatedSeconds (carries forward through resume cycles)
//  - in-flight:    accumulatedSeconds + (now − currentSegmentStart)
//  - never started + no accumulator: '—'
function fmtDuration(start, end, accumulatedSeconds) {
  const acc = accumulatedSeconds || 0;
  if (end) {
    if (acc > 0) return fmtSecs(acc);
    if (start) {
      const s = Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000));
      return fmtSecs(s);
    }
    return '—';
  }
  if (!start) return acc > 0 ? fmtSecs(acc) : '—';
  const live = Math.max(0, Math.round((Date.now() - new Date(start).getTime()) / 1000));
  return fmtSecs(acc + live);
}

const isTerminal = (s) => s === 'Completed' || s === 'Failed' || s === 'Cancelled';
// Once a cancel has been requested we stop polling — Cancelling can stick if the
// in-flight AI call ignores cancellation. Use Force Cancel + manual refresh from there.
const isPollingTerminal = (s) => isTerminal(s) || s === 'Cancelling';

export default function AutoEnrichmentJobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const jobId = parseInt(id, 10);
  const [job, setJob] = useState(null);
  const [items, setItems] = useState([]);
  const [itemTotal, setItemTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState(''); // '' = all
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ intervalSeconds: '', providerName: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [providers, setProviders] = useState([]);
  const pollRef = useRef(null);
  const theme = useTheme();
  const showInline = {
    leadId: useMediaQuery(theme.breakpoints.up('md')),
    statusFull: useMediaQuery(theme.breakpoints.up('md')),
    icpFitFull: useMediaQuery(theme.breakpoints.up('md')),
    suggestions: useMediaQuery(theme.breakpoints.up('sm')),
    leadStatus: useMediaQuery(theme.breakpoints.up('md')),
    icpMatch: useMediaQuery(theme.breakpoints.up('md')),
    error: useMediaQuery(theme.breakpoints.up('md')),
    times: useMediaQuery(theme.breakpoints.up('lg')),
  };
  const toggleRow = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  const [insightOpen, setInsightOpen] = useState(false);
  const [insightLead, setInsightLead] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const openInsight = async (leadId) => {
    setInsightOpen(true);
    setInsightLead(null);
    setInsightLoading(true);
    try {
      const { data } = await getLead(leadId);
      setInsightLead(data);
    } catch (e) {
      setInsightLead({ id: leadId, _error: e?.response?.data?.message || 'Failed to load lead' });
    } finally {
      setInsightLoading(false);
    }
  };

  const loadJob = useCallback(async () => {
    try {
      const { data } = await getAutoEnrichmentRun(jobId);
      setJob(data);
    } catch (e) {
      if (e?.response?.status === 404) setError('Job not found');
    }
  }, [jobId]);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await listAutoEnrichmentItems(jobId, {
        skip: page * rowsPerPage,
        take: rowsPerPage,
        status: statusFilter || undefined,
      });
      setItems(data || []);
    } catch { /* ignore */ }
  }, [jobId, page, rowsPerPage, statusFilter]);

  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAiProviders();
        setProviders(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
  }, []);

  // Auto-poll while non-terminal. Stops as soon as cancel is requested.
  useEffect(() => {
    if (!job) return undefined;
    if (isPollingTerminal(job.status)) return undefined;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      await loadJob();
      await loadItems();
    };

    pollRef.current = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [job, loadJob, loadItems]);

  // Item counts derived from job rollups. Skipped = processed - succeeded - failed.
  // Processing is ephemeral (~0/1); count it from current page rather than DTO.
  const statusCounts = job ? (() => {
    const total = job.total || 0;
    const processed = job.processed || 0;
    const succeeded = job.succeeded || 0;
    const failed = job.failed || 0;
    const skipped = Math.max(0, processed - succeeded - failed);
    const pending = Math.max(0, total - processed);
    const processing = items.filter((i) => i.status === 'Processing').length;
    return { all: total, Pending: pending, Processing: processing, Succeeded: succeeded, Failed: failed, Skipped: skipped };
  })() : { all: 0, Pending: 0, Processing: 0, Succeeded: 0, Failed: 0, Skipped: 0 };

  // Pagination total tracks the active filter so page count is correct.
  useEffect(() => {
    setItemTotal(statusFilter ? (statusCounts[statusFilter] || 0) : (statusCounts.all || 0));
  }, [statusFilter, statusCounts.all, statusCounts.Pending, statusCounts.Processing, statusCounts.Succeeded, statusCounts.Failed, statusCounts.Skipped]);

  const handleStatusChip = (s) => {
    setStatusFilter((cur) => (cur === s ? '' : s));
    setPage(0);
  };

  const handleCancel = async (force = false) => {
    const msg = force
      ? `Force-cancel job #${jobId}? Marks the job Cancelled immediately and skips every remaining lead. The in-flight AI call may still complete in the background but will not change job state.`
      : `Cancel job #${jobId}? Lead currently in flight finishes; remaining leads are skipped.`;
    if (!(await dialog.confirm({ title: force ? 'Force-cancel job' : 'Cancel job', message: msg, confirmText: force ? 'Force cancel' : 'Cancel job', danger: true }))) return;
    setCancelling(true);
    try {
      await cancelAutoEnrichmentRun(jobId, force);
      await loadJob();
      await loadItems();
    } catch (e) {
      setError(e?.response?.data?.message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  const handleResume = async (force = false) => {
    const msg = force
      ? `Force-resume job #${jobId}? Tears down any zombie/in-flight runner and starts a fresh pass over every non-succeeded lead.`
      : `Resume job #${jobId}? Re-processes every lead that did not succeed (Skipped + Failed).`;
    if (!(await dialog.confirm({ title: force ? 'Force-resume job' : 'Resume job', message: msg, confirmText: 'Resume' }))) return;
    setResuming(true);
    try {
      await resumeAutoEnrichmentRun(jobId, force);
      await loadJob();
      await loadItems();
    } catch (e) {
      setError(e?.response?.data?.message || 'Resume failed');
    } finally {
      setResuming(false);
    }
  };

  const handleManualRefresh = async () => {
    await loadJob();
    await loadItems();
  };

  const openEdit = () => {
    setEditForm({
      intervalSeconds: job?.intervalSeconds != null ? String(job.intervalSeconds) : '',
      providerName: job?.providerName || '',
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      const body = {};
      if (editForm.intervalSeconds !== '') body.intervalSeconds = parseInt(editForm.intervalSeconds, 10);
      if (editForm.providerName !== (job?.providerName || '')) body.providerName = editForm.providerName || '';
      await updateAutoEnrichmentRun(jobId, body);
      setEditOpen(false);
      await loadJob();
    } catch (e) {
      setError(e?.response?.data?.message || 'Update failed');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = await dialog.confirm({
      title: 'Delete enrichment job',
      message: `Delete job #${jobId}? This permanently removes the job and every per-lead result row. The leads themselves are not affected.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteAutoEnrichmentRun(jobId);
      navigate('/leads/auto-enrichment');
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
      setDeleting(false);
    }
  };

  if (!job) {
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ color: '#64748b' }}>Loading job…</Typography>
      </Box>
    );
  }

  const f = job.filter || {};
  const filterChips = [];
  if (f.search) filterChips.push(`search="${f.search}"`);
  if (f.status) filterChips.push(`status=${f.status}`);
  if (f.segmentationId) filterChips.push(`segmentation #${f.segmentationId}`);
  if (f.source) filterChips.push(`source=${f.source}`);
  if (f.idFrom != null) filterChips.push(`id≥${f.idFrom}`);
  if (f.idTo != null) filterChips.push(`id≤${f.idTo}`);
  if (f.createdAfter) filterChips.push(`after ${new Date(f.createdAfter).toLocaleDateString()}`);
  if (f.createdBefore) filterChips.push(`before ${new Date(f.createdBefore).toLocaleDateString()}`);
  if (f.limit) filterChips.push(`limit ${f.limit}`);
  if (f.includeArchived) filterChips.push('inc archived');
  if (filterChips.length === 0) filterChips.push('all leads');

  const showCancel = job.status === 'Pending' || job.status === 'Running' || job.status === 'Cancelling';
  const cancelDisabled = cancelling || job.status === 'Cancelling';
  const showForceCancel = showCancel; // Force-cancel is always an escape hatch while non-terminal.
  const showResume = job.status === 'Cancelled' || job.status === 'Failed';
  const showForceResume = showResume || job.status === 'Cancelling'; // Allow tearing down a stuck Cancelling job.
  const canDelete = isTerminal(job.status);
  const providerEditLocked = job.status === 'Running' || job.status === 'Cancelling';

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 1,
          rowGap: 1,
        }}
      >
        <IconButton size="small" onClick={() => navigate('/leads/auto-enrichment')}><BackIcon fontSize="small" /></IconButton>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          Auto Enrichment Job #{job.id}
        </Typography>
        <Chip
          size="small"
          label={STATUS_LABEL[job.status] || job.status}
          color={STATUS_CHIP_COLOR[job.status] || 'default'}
          icon={
            job.status === 'Running' ? <RunningIcon fontSize="small" /> :
            job.status === 'Cancelling' ? <CancellingIcon fontSize="small" /> :
            job.status === 'Completed' ? <DoneIcon fontSize="small" /> :
            job.status === 'Failed' ? <FailIcon fontSize="small" /> :
            job.status === 'Cancelled' ? <SkippedIcon fontSize="small" /> : undefined
          }
        />
        <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 8 } }} />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1,
            rowGap: 1,
            ml: { xs: 'auto', sm: 0 },
          }}
        >
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={handleManualRefresh}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
          {showCancel && (
            <Button size="small" color="warning" variant="outlined"
              startIcon={<CancelIcon />} onClick={() => handleCancel(false)} disabled={cancelDisabled}>
              {job.status === 'Cancelling' ? 'Cancel requested…' : (cancelling ? 'Cancelling…' : 'Cancel')}
            </Button>
          )}
          {showForceCancel && (
            <Tooltip title="Mark Cancelled immediately. Skips remaining leads even if the in-flight AI call ignores cancellation.">
              <Button size="small" color="error" variant="outlined"
                startIcon={<CancelIcon />} onClick={() => handleCancel(true)} disabled={cancelling}>
                Force cancel
              </Button>
            </Tooltip>
          )}
          {showResume && (
            <Button size="small" color="primary" variant="contained"
              startIcon={<ResumeIcon />} onClick={() => handleResume(false)} disabled={resuming}>
              {resuming ? 'Resuming…' : 'Resume'}
            </Button>
          )}
          {showForceResume && (
            <Tooltip title="Tear down any zombie runner and start a fresh pass over every non-succeeded lead.">
              <Button size="small" color="primary" variant="outlined"
                startIcon={<ResumeIcon />} onClick={() => handleResume(true)} disabled={resuming}>
                Force resume
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Edit interval / AI engine">
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openEdit}>
              Edit
            </Button>
          </Tooltip>
          {canDelete && (
            <Tooltip title="Delete job + its per-lead result rows. Leads are not affected.">
              <Button size="small" color="error" variant="outlined"
                startIcon={<DeleteIcon />} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2, borderRadius: 2 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, job.progressPercent ?? 0)}
          sx={{ mb: 1.5, height: 10, borderRadius: 1 }}
        />
        <Box
          sx={{
            color: '#475569',
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 3,
            rowGap: 0.75,
          }}
        >
          <Typography variant="body2">Total: <strong>{job.total}</strong></Typography>
          <Typography variant="body2">Processed: <strong>{job.processed}</strong> ({(job.progressPercent ?? 0).toFixed(1)}%)</Typography>
          <Typography variant="body2" sx={{ color: '#16a34a' }}>OK: <strong>{job.succeeded}</strong></Typography>
          <Typography variant="body2" sx={{ color: '#dc2626' }}>Failed: <strong>{job.failed}</strong></Typography>
          <Typography variant="body2">Duration: <strong>{fmtDuration(job.startedAt, job.finishedAt, job.accumulatedSeconds)}</strong></Typography>
          {(() => {
            const eta = computeEta(job);
            if (!eta) return null;
            return (
              <Tooltip title={`Estimated finish at ${eta.finishAt.toLocaleString()}. Based on average time per lead so far (includes inter-lead interval).`}>
                <Typography variant="body2">
                  Est. finish: <strong>~{eta.finishAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                  <span style={{ color: '#94a3b8' }}> ({fmtSecs(eta.remainingSecs)} left)</span>
                </Typography>
              </Tooltip>
            );
          })()}
        </Box>
        <Box
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: '1px dashed #e2e8f0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: '#64748b', mr: 0.5 }}>ICP fit:</Typography>
          <Tooltip title="Direct ICP match — routed to Qualified">
            <Chip size="small" variant="outlined" color="success"
              label={<span>Current <strong>{job.icpCurrentFit ?? 0}</strong></span>}
              sx={{ height: 22, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
          </Tooltip>
          <Tooltip title="Future potential — outreach-worthy. Routed to Qualified (or PartnerCandidate when partner=Yes).">
            <Chip size="small" variant="outlined" color="info"
              label={<span>Future <strong>{job.icpFuturePotential ?? 0}</strong></span>}
              sx={{ height: 22, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
          </Tooltip>
          <Tooltip title="Not a fit — routed to Disqualified / PartnerCandidate">
            <Chip size="small" variant="outlined" color="error"
              label={<span>Not fit <strong>{job.icpNotAFit ?? 0}</strong></span>}
              sx={{ height: 22, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
          </Tooltip>
          {(job.icpUnknown ?? 0) > 0 && (
            <Tooltip title="Verdict could not be parsed">
              <Chip size="small" variant="outlined"
                label={<span>Unknown <strong>{job.icpUnknown}</strong></span>}
                sx={{ height: 22, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
            </Tooltip>
          )}
        </Box>
        {job.errorMessage && <Alert severity="error" sx={{ mt: 1.5 }}>{job.errorMessage}</Alert>}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            color: '#475569',
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 4,
            rowGap: 1,
          }}
        >
          <Typography variant="body2">Provider: <strong>{job.providerName || '— default —'}</strong></Typography>
          <Typography variant="body2">Interval: <strong>{job.intervalSeconds}s</strong></Typography>
          <Typography variant="body2">Started: <strong>{job.startedAt ? new Date(job.startedAt).toLocaleString() : '—'}</strong></Typography>
          <Typography variant="body2">Finished: <strong>{job.finishedAt ? new Date(job.finishedAt).toLocaleString() : '—'}</strong></Typography>
          <Typography variant="body2">Created by: <strong>{job.createdByUsername || '—'}</strong></Typography>
        </Box>
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.75 }}>
          {filterChips.map((c) => <Chip key={c} size="small" label={c} variant="outlined" />)}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.75, mb: 1, px: { xs: 0.5, sm: 0 } }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', mr: 0.5 }}>
            Lead-by-lead results
          </Typography>
          {[
            { key: '', label: 'All', count: statusCounts.all, color: 'default' },
            { key: 'Pending', label: 'Pending', count: statusCounts.Pending, color: 'default' },
            { key: 'Processing', label: 'Processing', count: statusCounts.Processing, color: 'info' },
            { key: 'Succeeded', label: 'Succeeded', count: statusCounts.Succeeded, color: 'success' },
            { key: 'Failed', label: 'Failed', count: statusCounts.Failed, color: 'error' },
            { key: 'Skipped', label: 'Skipped', count: statusCounts.Skipped, color: 'warning' },
          ].map((c) => {
            const active = statusFilter === c.key;
            return (
              <Chip
                key={c.key || 'all'}
                size="small"
                clickable
                onClick={() => handleStatusChip(c.key)}
                color={active ? c.color : 'default'}
                variant={active ? 'filled' : 'outlined'}
                label={<span>{c.label} <strong>{c.count}</strong></span>}
                sx={{ height: 24, fontSize: 12, '& .MuiChip-label': { px: 0.85 } }}
              />
            );
          })}
        </Box>
        <Box sx={{ overflowX: 'auto', mx: { xs: -1, sm: 0 }, px: { xs: 0.5, sm: 0 } }}>
          <Table
            size="small"
            sx={{
              minWidth: { xs: 0, md: 900 },
              '& td, & th': {
                px: { xs: 0.75, sm: 1.5 },
              },
              '& th': { whiteSpace: 'nowrap' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 32, p: 0 }} />
                <TableCell sx={{ width: 72, display: { xs: 'none', md: 'table-cell' } }}>ID</TableCell>
                <TableCell>Company</TableCell>
                <TableCell sx={{ width: { xs: 44, md: 110 } }}>Status</TableCell>
                <TableCell sx={{ width: { xs: 52, md: 120 } }}>ICP</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Lead Status</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Sugg.</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>ICP %</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Started</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Finished</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => {
                const isOpen = !!expanded[it.id];
                // Always-rendered cells: chevron + Company + Status + ICP (4).
                // Conditional: ID (md+), LeadStatus (md+), Suggestions (sm+),
                // ICP Match % (md+), Started + Finished (lg+), Error (md+).
                const colSpanFull = 4
                  + (showInline.leadId ? 1 : 0)
                  + (showInline.leadStatus ? 1 : 0)
                  + (showInline.suggestions ? 1 : 0)
                  + (showInline.icpMatch ? 1 : 0)
                  + (showInline.times ? 2 : 0)
                  + (showInline.error ? 1 : 0);
                return (
                  <React.Fragment key={it.id}>
                    <TableRow hover sx={{ '& > td': { borderBottom: isOpen ? 'none' : undefined } }}>
                      <TableCell sx={{ p: 0, width: 32 }}>
                        <IconButton size="small" onClick={() => toggleRow(it.id)} aria-label={isOpen ? 'Hide' : 'Show'}>
                          {isOpen ? <CollapseUpIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>
                        <RouterLink to={`/leads/${it.leadId}`}>#{it.leadId}</RouterLink>
                      </TableCell>
                      <TableCell sx={{ maxWidth: { xs: 130, sm: 220, md: 280 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.companyName || <em style={{ color: '#94a3b8' }}>(deleted)</em>}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={it.status} disableHoverListener={showInline.statusFull}>
                          <Chip
                            size="small"
                            label={showInline.statusFull ? it.status : (ITEM_STATUS_SIGIL[it.status] || '?')}
                            color={ITEM_STATUS_COLOR[it.status] || 'default'}
                            sx={{
                              height: 22,
                              fontSize: 11,
                              '& .MuiChip-label': { px: showInline.statusFull ? 0.75 : 0 },
                              minWidth: showInline.statusFull ? undefined : 26,
                              fontWeight: showInline.statusFull ? undefined : 700,
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {it.aiIcpFit
                          ? (
                            <Tooltip title={it.aiIcpFit} disableHoverListener={showInline.icpFitFull}>
                              <Chip
                                size="small"
                                label={showInline.icpFitFull ? it.aiIcpFit : (ICP_FIT_SHORT[it.aiIcpFit] || '?')}
                                color={ICP_FIT_COLOR[it.aiIcpFit] || 'default'}
                                variant="outlined"
                                sx={{
                                  height: 22,
                                  fontSize: 11,
                                  '& .MuiChip-label': { px: showInline.icpFitFull ? 0.75 : 0.25 },
                                  minWidth: showInline.icpFitFull ? undefined : 32,
                                  fontWeight: showInline.icpFitFull ? undefined : 700,
                                }}
                              />
                            </Tooltip>
                          )
                          : <span style={{ color: '#94a3b8' }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {it.leadStatus
                          ? <Chip size="small" label={it.leadStatus} variant="outlined" />
                          : <span style={{ color: '#94a3b8' }}>—</span>}
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{it.suggestionsApplied}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{it.icpMatchPercentage != null ? `${it.icpMatchPercentage}%` : '—'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{it.startedAt ? new Date(it.startedAt).toLocaleTimeString() : '—'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{it.finishedAt ? new Date(it.finishedAt).toLocaleTimeString() : '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 320, color: '#dc2626', fontSize: 12, display: { xs: 'none', md: 'table-cell' } }}>
                        {it.errorMessage || ''}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={colSpanFull} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ px: { xs: 1.25, sm: 2 }, py: 1.25, bgcolor: '#f8fafc' }}>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
                                rowGap: 1,
                                columnGap: 2,
                                fontSize: 12,
                                color: '#475569',
                              }}
                            >
                              {!showInline.leadId && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Lead ID</Typography>
                                  <RouterLink to={`/leads/${it.leadId}`}>#{it.leadId}</RouterLink>
                                </Box>
                              )}
                              {!showInline.statusFull && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Status</Typography>
                                  <Chip size="small" label={it.status} color={ITEM_STATUS_COLOR[it.status] || 'default'} sx={{ mt: 0.25 }} />
                                </Box>
                              )}
                              {!showInline.icpFitFull && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>ICP Fit</Typography>
                                  {it.aiIcpFit
                                    ? <Chip size="small" label={it.aiIcpFit} color={ICP_FIT_COLOR[it.aiIcpFit] || 'default'} variant="outlined" sx={{ mt: 0.25 }} />
                                    : <span style={{ color: '#94a3b8' }}>—</span>}
                                </Box>
                              )}
                              {!showInline.leadStatus && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Lead Status</Typography>
                                  {it.leadStatus
                                    ? <Chip size="small" label={it.leadStatus} variant="outlined" sx={{ mt: 0.25 }} />
                                    : <span style={{ color: '#94a3b8' }}>—</span>}
                                </Box>
                              )}
                              {!showInline.suggestions && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Suggestions</Typography>
                                  <strong>{it.suggestionsApplied}</strong>
                                </Box>
                              )}
                              {!showInline.icpMatch && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>ICP Match %</Typography>
                                  <strong>{it.icpMatchPercentage != null ? `${it.icpMatchPercentage}%` : '—'}</strong>
                                </Box>
                              )}
                              {!showInline.times && (
                                <>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Started</Typography>
                                    <strong>{it.startedAt ? new Date(it.startedAt).toLocaleTimeString() : '—'}</strong>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Finished</Typography>
                                    <strong>{it.finishedAt ? new Date(it.finishedAt).toLocaleTimeString() : '—'}</strong>
                                  </Box>
                                </>
                              )}
                              {!showInline.error && it.errorMessage && (
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Error</Typography>
                                  <Typography variant="body2" sx={{ color: '#dc2626', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {it.errorMessage}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<InsightIcon fontSize="small" sx={{ color: '#6366f1' }} />}
                                onClick={() => openInsight(it.leadId)}
                              >
                                View AI insights
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} sx={{ color: '#94a3b8', textAlign: 'center' }}>
                    {statusFilter ? `No ${statusFilter} items.` : 'No items.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={itemTotal}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100]}
          sx={{
            '.MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1, pl: { xs: 1, sm: 2 } },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { mb: 0 },
          }}
        />
      </Paper>

      <Dialog open={insightOpen} onClose={() => setInsightOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <InsightIcon fontSize="small" sx={{ color: '#6366f1' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {insightLead?.companyName || (insightLoading ? 'Loading…' : 'Lead insights')}
            </Typography>
            {insightLead && (
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                <RouterLink to={`/leads/${insightLead.id}`}>#{insightLead.id}</RouterLink>
                {insightLead.aiInsightsProvider ? ` · ${insightLead.aiInsightsProvider}` : ''}
                {insightLead.aiInsightsUpdatedAt ? ` · ${new Date(insightLead.aiInsightsUpdatedAt).toLocaleString()}` : ''}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {insightLoading && <Typography variant="body2" sx={{ color: '#64748b' }}>Loading insights…</Typography>}
          {!insightLoading && insightLead?._error && (
            <Alert severity="error">{insightLead._error}</Alert>
          )}
          {!insightLoading && insightLead && !insightLead._error && !insightLead.aiInsights && (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              No AI insights yet for this lead.
            </Typography>
          )}
          {!insightLoading && insightLead?.aiInsights && (
            <Box sx={{ '& > *:first-of-type': { mt: 0 } }}>
              {insightLead.aiInsights.split('\n').map((line, i) => {
                if (line.startsWith('## ')) {
                  return <Typography key={i} variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: '#1f2937' }}>{line.slice(3)}</Typography>;
                }
                if (line.startsWith('# ')) {
                  return <Typography key={i} variant="subtitle1" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>{line.slice(2)}</Typography>;
                }
                if (line.startsWith('- ') || line.startsWith('* ')) {
                  return (
                    <Box key={i} sx={{ display: 'flex', gap: 1, ml: 0.5, fontSize: '0.85rem', lineHeight: 1.6, color: '#1f2937' }}>
                      <span>•</span>
                      <span dangerouslySetInnerHTML={{
                        __html: line.slice(2)
                          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
                          .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>'),
                      }} />
                    </Box>
                  );
                }
                if (!line.trim()) return <Box key={i} sx={{ height: 6 }} />;
                return (
                  <Typography key={i} variant="body2" sx={{ fontSize: '0.85rem', lineHeight: 1.6, color: '#1f2937' }}
                    dangerouslySetInnerHTML={{
                      __html: line
                        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
                        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>'),
                    }}
                  />
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {insightLead && !insightLead._error && (
            <Button onClick={() => navigate(`/leads/${insightLead.id}`)}>Open lead</Button>
          )}
          <Button onClick={() => setInsightOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit job #{job.id}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              label="Interval between leads (sec)"
              size="small"
              type="number"
              inputProps={{ min: 0, max: 3600 }}
              value={editForm.intervalSeconds}
              onChange={(e) => setEditForm((f) => ({ ...f, intervalSeconds: e.target.value }))}
              helperText="Picked up on the next inter-lead delay."
            />
            <TextField
              label="AI engine"
              size="small"
              select
              value={editForm.providerName}
              onChange={(e) => setEditForm((f) => ({ ...f, providerName: e.target.value }))}
              disabled={providerEditLocked}
              helperText={providerEditLocked
                ? 'Cancel the job before changing the engine.'
                : 'Used on resume / next run.'}
            >
              <MenuItem value="">— default —</MenuItem>
              {(providers.length > 0
                ? providers.map((p) => ({ value: (p.name || '').toLowerCase(), label: p.displayName || p.name }))
                : [
                    { value: 'gemini', label: 'Gemini (default)' },
                    { value: 'openai', label: 'OpenAI' },
                  ]
              ).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
